/**
 * WC 2026 team ID registry.
 *
 * Populated once when WorldCupDisplayPage loads its groups data, then
 * persisted to localStorage so any page (TeamPage, MatchViewModal, etc.)
 * can check membership without needing the WC page to have been visited
 * in the same session.
 *
 * Usage:
 *   import { isWcTeam, setWcTeamIds } from '../lib/wcTeams';
 *   const src = isWcTeam(teamId) ? wcPlayerUrl(playerId) : playerUrl(playerId);
 */

// v2: bumped because the display page was briefly repointed at the Premier
// League via site settings, which poisoned v1's persisted set with every PL
// club's team ID (see WorldCupDisplayPage's fetchGroups) — this throws away
// any such stale data still sitting in visitors' browsers.
const STORAGE_KEY = 'wc2026_team_ids_v2';

// Seed from localStorage so the check works on any page immediately
const _ids = new Set(
  (() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch { return []; }
  })()
);

/**
 * Call this once when you have the full list of WC team IDs (e.g. from groups data).
 * Merges with any existing IDs and persists.
 */
export function setWcTeamIds(ids) {
  ids.forEach(id => _ids.add(String(id)));
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify([..._ids])); } catch {}
}

/** Returns true if this team is in the WC 2026 squad. */
export function isWcTeam(teamId) {
  return !!teamId && _ids.has(String(teamId));
}

// 3-letter codes (FIFA-style) for compact displays like the knockout bracket.
const _ABBR = {
  'south africa': 'RSA', 'canada': 'CAN', 'germany': 'GER', 'paraguay': 'PAR',
  'netherlands': 'NED', 'morocco': 'MAR', 'brazil': 'BRA', 'japan': 'JPN',
  'france': 'FRA', 'sweden': 'SWE', 'mexico': 'MEX', 'ecuador': 'ECU',
  'england': 'ENG', 'congo dr': 'COD', 'dr congo': 'COD', 'belgium': 'BEL',
  'senegal': 'SEN', 'usa': 'USA', 'bosnia-herzegovina': 'BIH', 'bosnia and herzegovina': 'BIH',
  'spain': 'ESP', 'austria': 'AUT', 'portugal': 'POR', 'croatia': 'CRO',
  'switzerland': 'SUI', 'algeria': 'ALG', 'australia': 'AUS', 'egypt': 'EGY',
  'argentina': 'ARG', 'cape verde': 'CPV', 'colombia': 'COL', 'ghana': 'GHA',
  'ivory coast': 'CIV', 'norway': 'NOR',
  // other WC2026 teams (in case of different routings)
  'iran': 'IRN', 'uzbekistan': 'UZB', 'qatar': 'QAT', 'saudi arabia': 'KSA',
  'jordan': 'JOR', 'south korea': 'KOR', 'scotland': 'SCO', 'wales': 'WAL',
  'tunisia': 'TUN', 'uruguay': 'URU', 'panama': 'PAN', 'curacao': 'CUW',
  'haiti': 'HAI', 'new zealand': 'NZL', 'iraq': 'IRQ', 'turkey': 'TUR',
};

/**
 * Short code for a team name, for compact UI (bracket boxes).
 * Resolves knockout placeholders too: "Winner Match 73" → "W73", "Loser Match 102" → "L102".
 */
export function teamAbbr(name) {
  if (!name) return '';
  const n = name.trim();
  const ph = n.match(/^(winner|loser)\s+match\s+(\d+)/i);
  if (ph) return (ph[1][0].toUpperCase()) + ph[2];           // W73 / L102
  const ru = n.match(/^runner-?up\s+match\s+(\d+)/i);
  if (ru) return 'RU' + ru[1];
  const hit = _ABBR[n.toLowerCase()];
  if (hit) return hit;
  // Fallback: first three letters of the last significant word, uppercased
  return n.replace(/[^A-Za-z ]/g, '').trim().slice(0, 3).toUpperCase() || '?';
}
